import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, User as UserIcon, CreditCard } from "lucide-react";
import api, { fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import VoiceRecorder from "@/components/VoiceRecorder";
import PaymentHub from "@/components/PaymentHub";
import PhotoUploader from "@/components/PhotoUploader";
import { useT } from "@/lib/i18n";

const QUICK_PROMPTS_BY_LANG = {
  english: [
    "I sold 3 bags of rice ₦45,000",
    "Customer Ibrahim took ₦15k on credit, he go pay Friday",
    "Bought fuel ₦5k for gen",
    "How much I sell this week?",
  ],
  pidgin: [
    "I sell 3 bags rice ₦45,000",
    "Customer Ibrahim collect ₦15k on credit, e go pay Friday",
    "I buy fuel ₦5k for gen",
    "How much I don sell this week?",
  ],
  yoruba: [
    "Mo ta ìgò ìrẹsì mẹ́ta ₦45,000",
    "Ibrahim gba ₦15k láìsanwó, ó máa san ní Friday",
    "Mo ra epo ₦5k fún gen",
    "Èló ni mo ti tà ní ọsẹ̀ yìí?",
  ],
  hausa: [
    "Na sayar da buhu uku na shinkafa ₦45,000",
    "Ibrahim ya karɓi ₦15k bashi, zai biya ranar Juma'a",
    "Na sayi mai ₦5k na janareta",
    "Nawa na sayar a wannan makon?",
  ],
  igbo: [
    "Eresịrị m akpa osikapa atọ ₦45,000",
    "Ibrahim nara ₦15k n'akwụghị ụgwọ, ọ ga-akwụ Friday",
    "Azụrụ m mmanụ ₦5k maka jenereto",
    "Ego ole ka m rere n'izu a?",
  ],
};

export default function Chat() {
  const { user } = useAuth();
  const t = useT(user?.preferred_language);
  const lang = user?.preferred_language || "english";
  const QUICK_PROMPTS = QUICK_PROMPTS_BY_LANG[lang] || QUICK_PROMPTS_BY_LANG.english;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPay, setShowPay] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = async () => {
    try {
      const r = await api.get("/chat/history");
      setMessages(r.data.messages || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    // Poll every 15s to pull in WhatsApp messages that arrived while the user
    // has this page open. Only poll while the tab is visible.
    const iv = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      loadHistory();
    }, 15000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    // optimistic
    const tempUser = { id: "tmp-" + Date.now(), role: "user", text: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tempUser]);
    setSending(true);
    try {
      const r = await api.post("/chat/message", { text: msg });
      setMessages((m) => {
        // replace tempUser with real, add assistant
        const filtered = m.filter((x) => x.id !== tempUser.id);
        return [...filtered, r.data.user_message, r.data.assistant_message];
      });
    } catch (e) {
      toast.error("Network slow, try again");
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
    } finally {
      setSending(false);
    }
  };

  const handleVoiceResult = (data) => {
    if (data?.user_message && data?.assistant_message) {
      setMessages((m) => [...m, data.user_message, data.assistant_message]);
    } else if (!data?.transcript) {
      toast.error("No speech detected");
    }
  };

  return (
    <div className="h-full flex flex-col chat-bg">
      {/* Header */}
      <div className="bg-kudi-green text-white px-4 py-3 flex items-center gap-3 shadow-sm">
        <div className="w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold leading-tight">Kobolog AI</div>
          <div className="text-xs text-white/75">Every kobo, logged.</div>
        </div>
        <Link to="/app/profile" className="w-9 h-9 rounded-full bg-white/15 border border-white/20 flex items-center justify-center hover:bg-white/25 transition" data-testid="profile-link">
          <UserIcon className="w-4 h-4" />
        </Link>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 no-scrollbar" data-testid="chat-messages">
        {loading ? (
          <div className="text-center text-kudi-ink/50 text-sm py-10">Loading...</div>
        ) : messages.length === 0 ? (
          <EmptyState onPrompt={send} ownerName={user?.owner_name} quickPrompts={QUICK_PROMPTS} greetingText={t("welcome")} lang={lang} helperText={lang === "english" ? "Tell me what you sold, bought, or who took goods on credit. I'll handle the books." : lang === "pidgin" ? "Yarn me wetin you sell, buy, or wetin people collect for credit. I go handle the books." : "Type or talk — I'll log everything."} />
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((m) => <Bubble key={m.id} m={m} />)}
            {sending && <TypingBubble />}
          </div>
        )}
      </div>

      {/* Quick prompts */}
      {messages.length <= 2 && !sending && (
        <div className="px-3 pb-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="quick-prompts">
          {QUICK_PROMPTS.map((p, i) => (
            <button
              key={p}
              onClick={() => send(p)}
              className="shrink-0 bg-white border border-kudi-green/40 text-kudi-green px-3 py-1.5 rounded-full text-xs font-medium hover:bg-accent transition"
              data-testid={`quick-prompt-${i}`}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border bg-white px-3 py-2 flex items-center gap-2">
        <button
          onClick={() => setShowPay(true)}
          className="h-10 px-2.5 rounded-full bg-accent flex items-center gap-1 text-kudi-green hover:bg-accent/70 transition shrink-0"
          data-testid="get-paid-btn"
          title="Receive money — DVA, cashtag, link or QR"
          aria-label="Receive money"
        >
          <CreditCard className="w-5 h-5" />
          <span className="text-[11px] font-semibold pr-1">Receive</span>
        </button>
        <PhotoUploader onResult={handleVoiceResult} disabled={sending} />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={t("chatPlaceholder")}
          className="flex-1 bg-accent border-0 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-kudi-green/20"
          data-testid="chat-input"
        />
        {input.trim() ? (
          <button
            onClick={() => send()}
            disabled={sending}
            className="w-10 h-10 rounded-full bg-kudi-green text-white flex items-center justify-center hover:bg-kudi-green-dark transition disabled:opacity-40 active:scale-95"
            data-testid="send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        ) : (
          <VoiceRecorder onResult={handleVoiceResult} disabled={sending} />
        )}
      </div>
      {showPay && <PaymentHub onClose={() => setShowPay(false)} />}
    </div>
  );
}

function Bubble({ m }) {
  const isUser = m.role === "user";
  const isWA = m.source === "whatsapp";
  return (
    <div className={`flex bubble-in ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`${
          isUser
            ? "bg-kudi-green text-white rounded-2xl rounded-br-[6px]"
            : "bg-white text-kudi-ink rounded-2xl rounded-bl-[6px] border border-border/60"
        } px-3.5 py-2.5 max-w-[82%] shadow-sm text-[15px] leading-relaxed`}
        data-testid={`msg-${m.role}`}
      >
        <div className="whitespace-pre-wrap break-words">{m.text}</div>
        <div className={`mt-1 text-[10px] ${isUser ? "text-white/60" : "text-kudi-ink/40"} text-right flex items-center gap-1 justify-end`}>
          {isWA && (
            <span
              className={`inline-flex items-center gap-0.5 px-1 py-[1px] rounded ${isUser ? "bg-white/15" : "bg-emerald-50 text-emerald-700"}`}
              title="From WhatsApp"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.52 3.48A11.9 11.9 0 0012.05 0C5.52 0 .18 5.32.18 11.87c0 2.09.55 4.14 1.6 5.94L0 24l6.34-1.66a11.86 11.86 0 005.69 1.45h.01c6.53 0 11.87-5.32 11.87-11.87 0-3.17-1.24-6.15-3.39-8.44zM12.04 21.6h-.01a9.72 9.72 0 01-4.96-1.36l-.36-.21-3.76.98 1-3.67-.24-.38a9.7 9.7 0 01-1.5-5.19c0-5.37 4.4-9.74 9.83-9.74 2.63 0 5.1 1.02 6.96 2.87a9.62 9.62 0 012.87 6.87c-.01 5.37-4.41 9.83-9.83 9.83z"/></svg>
              WA
            </span>
          )}
          <span>{fmtTime(m.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start bubble-in">
      <div className="bg-white border border-border/60 rounded-2xl rounded-bl-[6px] px-4 py-3 shadow-sm flex items-center gap-1.5">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    </div>
  );
}

function EmptyState({ onPrompt, ownerName, quickPrompts, greetingText, helperText, lang }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10">
      <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center text-kudi-green mb-4">
        <Sparkles className="w-7 h-7" />
      </div>
      <h2 className="font-heading text-xl font-bold">{greetingText} {ownerName || (lang === "pidgin" ? "boss" : "there")} 👋</h2>
      <p className="mt-2 text-sm text-kudi-ink/60 max-w-xs">{helperText}</p>
      <div className="mt-6 grid gap-2 w-full max-w-xs">
        {(quickPrompts || []).slice(0, 3).map((p, i) => (
          <button
            key={p}
            onClick={() => onPrompt(p)}
            className="bg-white border border-border rounded-2xl px-4 py-3 text-sm text-left hover:border-kudi-green/40 transition"
            data-testid={`empty-prompt-${i}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
